import { useState } from 'react'
import {
  Download,
  FileStack,
  Layers,
  Paperclip,
  Send,
  Upload,
} from 'lucide-react'
import { download, uploadWithRefresh, write } from '../api'
import { useRoleLabels } from '../roleLabels'
import { bytes, dateTime, shortHash, words } from '../format'
import {
  Button,
  Chip,
  Empty,
  Field,
  Modal,
  Panel,
  Progress,
  Refusal,
} from '../ui'
import { useResource } from '../useResource'

// ======================================================================
// EVIDENCE
// ======================================================================
//
// SUPERSEDED ITEMS STAY IN THE LIST. er_evidence is append-only: there
// is no PATCH and no DELETE on the router, because "what did you hold,
// and when" is the question a tribunal asks. A replaced exhibit carries
// supersedes_id and superseded_reason and is shown as replaced, not
// hidden.
//
// sha256 IS SHOWN, in full on hover and copyable. It is the answer to
// "is the file I am holding the file that was attached", and the person
// most likely to need to ask is the one defending the procedure.
//
// THERE IS NO storage_key ANYWHERE IN THIS FILE. The response does not
// carry one. The download route resolves the ACL per request and looks
// the key up itself, so no client ever needs it.

export default function Evidence({
  caseId,
  items,
  allegations,
  capabilities,
  parties,
  onReplace,
}) {
  const [dialog, setDialog] = useState(null)
  const canUpload = capabilities.includes('upload_evidence')
  const canDisclose = capabilities.includes('control_disclosure')

  // Which ids have been replaced by a later row.
  const supersededBy = new Map()
  items.forEach((e) => {
    if (e.supersedes_id) supersededBy.set(e.supersedes_id, e)
  })

  return (
    <Panel
      eyebrow="Exhibits"
      title="Evidence"
      right={
        <>
          {canDisclose && items.length > 0 && (
            <Button size="sm" icon={Send} onClick={() => setDialog({ kind: 'disclose' })}>
              Send to a party
            </Button>
          )}
          {canUpload && (
            <Button size="sm" icon={Upload} onClick={() => setDialog({ kind: 'upload' })}>
              Attach
            </Button>
          )}
        </>
      }
    >
      {items.length === 0 ? (
        <Empty icon={Paperclip} title="Nothing has been attached">
          {canUpload
            ? 'Any file type is accepted, up to 250 MB. Files are always sent to a browser as a download, never rendered.'
            : 'No exhibits are held on this case.'}
        </Empty>
      ) : (
        <ul className="space-y-2.5">
          {items.map((e) => (
            <EvidenceRow
              key={e.evidence_id}
              caseId={caseId}
              item={e}
              replacedBy={supersededBy.get(e.evidence_id)}
              allegations={allegations}
              canUpload={canUpload}
              onSupersede={() => setDialog({ kind: 'supersede', item: e })}
            />
          ))}
        </ul>
      )}

      {dialog?.kind === 'upload' && (
        <UploadDialog
          caseId={caseId}
          allegations={allegations}
          knownKinds={[...new Set(items.map((e) => e.kind))]}
          onClose={() => setDialog(null)}
          onReplace={onReplace}
        />
      )}
      {dialog?.kind === 'supersede' && (
        <SupersedeDialog
          caseId={caseId}
          item={dialog.item}
          onClose={() => setDialog(null)}
          onReplace={onReplace}
        />
      )}
      {dialog?.kind === 'disclose' && (
        <DiscloseEvidenceDialog
          caseId={caseId}
          items={items}
          parties={parties}
          onClose={() => setDialog(null)}
        />
      )}
    </Panel>
  )
}

function EvidenceRow({ caseId, item, replacedBy, allegations, canUpload, onSupersede }) {
  const [progress, setProgress] = useState(null)
  const [error, setError] = useState(null)
  const allegation = allegations?.find((a) => a.allegation_id === item.allegation_id)

  const fetchIt = async () => {
    setError(null)
    setProgress(0)
    try {
      // The filename and the size are passed IN rather than read from
      // the response headers, which this origin cannot see — see the
      // CORS note on download() in lib/api.js. Both come from the
      // evidence row the server built the headers from.
      await download(`/${caseId}/evidence/${item.evidence_id}/download`, {
        onProgress: setProgress,
        filename: item.original_filename,
        totalBytes: item.byte_size,
      })
    } catch (e) {
      setError(e)
    } finally {
      setProgress(null)
    }
  }

  return (
    <li className={`panel-inset px-4 py-3.5 ${replacedBy ? 'opacity-70' : ''}`}>
      <Refusal error={error} onDismiss={() => setError(null)} />

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="break-all font-head text-[13px] font-semibold">
            {item.original_filename}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Chip>{words(item.kind)}</Chip>
            {allegation && <Chip tone="blue">allegation #{allegation.ordinal}</Chip>}
            {item.supersedes_id && <Chip tone="amber">replaces an earlier exhibit</Chip>}
            {replacedBy && (
              <Chip tone="amber" title={replacedBy.original_filename}>
                replaced by a later exhibit
              </Chip>
            )}
            {item.recording_consent_by && (
              <Chip tone="accent" title={dateTime(item.recording_consent_at)}>
                consent: {item.recording_consent_by}
              </Chip>
            )}
          </div>

          {item.superseded_reason && (
            <p className="mt-2 text-[12px] leading-relaxed text-muted">
              <span className="text-faint">Reason it replaced the earlier item: </span>
              {item.superseded_reason}
            </p>
          )}
          {replacedBy?.superseded_reason && (
            <p className="mt-2 text-[12px] leading-relaxed text-muted">
              <span className="text-faint">Reason it was replaced: </span>
              {replacedBy.superseded_reason}
            </p>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-faint">
            <span>{bytes(item.byte_size)}</span>
            {item.content_type && <span>{item.content_type}</span>}
            <span>Attached {dateTime(item.uploaded_at)}</span>
            <span className="mono" title={item.sha256}>
              sha256 {shortHash(item.sha256)}
            </span>
          </div>

          {progress != null && (
            <div className="mt-3 max-w-xs">
              <Progress value={progress} label="Downloading" />
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col gap-1">
          <Button
            size="sm"
            icon={Download}
            busy={progress != null}
            onClick={fetchIt}
          >
            Download
          </Button>
          {canUpload && !replacedBy && (
            <Button size="sm" variant="ghost" icon={Layers} onClick={onSupersede}>
              Replace
            </Button>
          )}
        </div>
      </div>
    </li>
  )
}

/** UPLOAD, WITH REAL PROGRESS AND NO FROZEN TAB.
 *
 *  Uses XMLHttpRequest because fetch() reports no upload progress. The
 *  file is streamed by the browser, so a 250 MB recording never enters a
 *  JavaScript buffer here and the main thread stays free — the tab keeps
 *  scrolling while it goes.
 *
 *  THE SIZE IS NOT PRE-CHECKED. The 250 MB cap lives in blob.put(),
 *  which refuses AFTER the handler is entered so the attempt is written
 *  to the audit chain. Refusing here would be a silent rejection with no
 *  record that an oversized file was nearly attached. */
function UploadDialog({ caseId, allegations, knownKinds, onClose, onReplace }) {
  // The tenant's own word for each role (task 27). Applied at render;
  // the key is what the API sends and what a grant is written with.
  const { label: roleLabel } = useRoleLabels()

  const [file, setFile] = useState(null)
  const [kind, setKind] = useState('')
  const [allegationId, setAllegationId] = useState('')
  const [reason, setReason] = useState('')
  const [consent, setConsent] = useState('')
  const [progress, setProgress] = useState(null)
  const [error, setError] = useState(null)

  // One configuration read when the dialog opens. It names no case.
  const { data: vocab } = useResource('/vocabularies')
  const kinds = vocab?.evidence_kind ?? []

  const submit = async () => {
    setError(null)
    setProgress(0)
    const fd = new FormData()
    if (file) fd.append('file', file)
    if (kind.trim()) fd.append('kind', kind.trim())
    if (allegationId) fd.append('allegation_id', allegationId)
    if (reason.trim()) fd.append('reason', reason.trim())
    if (consent.trim()) fd.append('recording_consent_by', consent.trim())
    try {
      const next = await uploadWithRefresh(`/${caseId}/evidence`, fd, {
        onProgress: setProgress,
      })
      onReplace(next)
      onClose()
    } catch (e) {
      setError(e)
      setProgress(null)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Attach evidence"
      subtitle="Any file type, up to 250 MB. Nothing is rendered in the browser — an exhibit is always downloaded."
    >
      <Refusal error={error} onDismiss={() => setError(null)} />

      <div className="space-y-4">
        <Field label="File" required>
          <input
            type="file"
            className="field file:mr-3 file:rounded-full file:border-0 file:bg-accent file:px-3 file:py-1 file:font-head file:text-[11.5px] file:font-semibold file:text-[color:var(--accent-ink)]"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>

        {/* A PICKER SINCE TASK 18. The kind vocabulary lives in
            ck_er_evidence_kind and GET /vocabularies now reads it out of
            the catalog per request, so these options are what the
            database will accept rather than what has happened to be
            used on this case before.

            THE OLD BEHAVIOUR IS THE FALLBACK, not a deletion: if the
            fetch failed, the field is free text with the kinds already
            on this case as suggestions, exactly as it was, and the
            service's refusal still names every legal one. Attaching
            evidence is not something to block because a configuration
            read did not come back. */}
        <Field
          label="Kind"
          required
          hint={
            kinds.length
              ? 'What this file is, for the disclosure register and the bundle.'
              : 'If this is not one of the accepted kinds, the refusal will list them.'
          }
        >
          {kinds.length ? (
            <select
              className="field"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="">Choose a kind…</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {words(k)}
                </option>
              ))}
            </select>
          ) : (
            <>
              <input
                className="field"
                list="er-evidence-kinds"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
              />
              <datalist id="er-evidence-kinds">
                {knownKinds.map((k) => (
                  <option key={k} value={k} />
                ))}
              </datalist>
            </>
          )}
        </Field>

        {kind.trim() === 'recording' && (
          <Field
            label="Who consented to being recorded"
            required
            hint="A recording with no consent recorded against it is a liability rather than evidence."
          >
            <input
              className="field"
              value={consent}
              onChange={(e) => setConsent(e.target.value)}
            />
          </Field>
        )}

        {allegations?.length > 0 && (
          <Field label="Attach to an allegation" hint="Optional.">
            <select
              className="field"
              value={allegationId}
              onChange={(e) => setAllegationId(e.target.value)}
            >
              <option value="">Not linked to a particular allegation</option>
              {allegations.map((a) => (
                <option key={a.allegation_id} value={a.allegation_id}>
                  #{a.ordinal} — {a.body.slice(0, 70)}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Note" hint="Optional. Recorded with the attachment.">
          <input
            className="field"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </div>

      {progress != null && (
        <div className="mt-4">
          <Progress value={progress} label="Uploading" />
        </div>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={progress != null}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={progress != null}
          disabled={!file || !kind.trim()}
          onClick={submit}
        >
          Attach
        </Button>
      </div>
    </Modal>
  )
}

function SupersedeDialog({ caseId, item, onClose, onReplace }) {
  const [file, setFile] = useState(null)
  const [reason, setReason] = useState('')
  const [progress, setProgress] = useState(null)
  const [error, setError] = useState(null)

  const submit = async () => {
    setError(null)
    setProgress(0)
    const fd = new FormData()
    if (file) fd.append('file', file)
    if (reason.trim()) fd.append('reason', reason.trim())
    try {
      const next = await uploadWithRefresh(
        `/${caseId}/evidence/${item.evidence_id}/supersede`,
        fd,
        { onProgress: setProgress },
      )
      onReplace(next)
      onClose()
    } catch (e) {
      setError(e)
      setProgress(null)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Replace this exhibit"
      subtitle={`The original stays on the record. ${item.original_filename} is not deleted, altered or hidden — a new row is added that points back to it.`}
    >
      <Refusal error={error} onDismiss={() => setError(null)} />
      <div className="space-y-4">
        <Field label="Replacement file" required>
          <input
            type="file"
            className="field file:mr-3 file:rounded-full file:border-0 file:bg-accent file:px-3 file:py-1 file:font-head file:text-[11.5px] file:font-semibold file:text-[color:var(--accent-ink)]"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>
        <Field
          label="Why it is being replaced"
          required
          hint="Recorded on the replacement and shown alongside both items."
        >
          <textarea
            className="field field-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </div>
      {progress != null && (
        <div className="mt-4">
          <Progress value={progress} label="Uploading" />
        </div>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={progress != null}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={progress != null}
          disabled={!file || !reason.trim()}
          onClick={submit}
        >
          Replace
        </Button>
      </div>
    </Modal>
  )
}

/** ======================================================================
 *  THE REDACTION CONTROL. READ THIS BEFORE CHANGING ANY WORD OF IT.
 *  ======================================================================
 *
 *  `redacted` RECORDS THAT THE ITEM BEING SENT IS ALREADY A REDACTED
 *  VERSION OF SOMETHING. It does not ask this system to redact anything,
 *  and nothing in this codebase can: er_cases/disclosure.py stores the
 *  object byte-identically to what was uploaded, and the download route
 *  streams those same bytes.
 *
 *  So the control reads "this item is already redacted" and never
 *  "redact this item". The difference is not pedantry. A control
 *  labelled "redact" invites somebody to tick it while sending an
 *  unredacted original, believing the system will do the work. Nothing
 *  would happen to the file, the disclosure register would carry a
 *  statement that a redacted version was sent, and NOTHING IN THE SYSTEM
 *  CAN DETECT THAT — the hash matches, the file is intact, and the only
 *  false thing is the register entry that a tribunal would later rely
 *  on.
 *  ====================================================================== */
function DiscloseEvidenceDialog({ caseId, items, parties, onClose }) {
  // The tenant's own word for each role (task 27). See
  // components/cases/roleLabels.js — applied at render, never sent.
  const { label: roleLabel } = useRoleLabels()

  const [selected, setSelected] = useState([])
  const [recipient, setRecipient] = useState('')
  const [redacted, setRedacted] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))

  const live = (parties || []).filter((p) => !p.revoked_at)

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      await write(`/${caseId}/disclosures`, {
        body: {
          evidence_ids: selected,
          recipient_id: recipient,
          redacted,
          ...(note.trim() ? { redaction_note: note.trim() } : {}),
        },
      })
      onClose()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Send evidence to a party"
      subtitle="Recorded in the disclosure register. The register also records when they first open it."
      width="max-w-2xl"
    >
      <Refusal error={error} onDismiss={() => setError(null)} />

      <Field label="Who is being sent it" required>
        {live.length ? (
          <select
            className="field"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
          >
            <option value="">Choose a party…</option>
            {live.map((p) => (
              <option key={p.party_id} value={p.user_id}>
                {p.user_label || p.user_id} — {roleLabel(p.case_role)}
              </option>
            ))}
          </select>
        ) : (
          <input
            className="field"
            placeholder="User id"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
          />
        )}
      </Field>

      <div className="mt-4">
        <Field label="Which items" required>
          <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
            {items.map((e) => (
              <label
                key={e.evidence_id}
                className={`flex cursor-pointer items-start gap-3 rounded-md border px-3.5 py-2.5 transition ${
                  selected.includes(e.evidence_id)
                    ? 'border-accent-line bg-accent-dim'
                    : 'border-line bg-surface-2 hover:border-line-strong'
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 accent-[color:var(--accent)]"
                  checked={selected.includes(e.evidence_id)}
                  onChange={() => toggle(e.evidence_id)}
                />
                <span className="min-w-0 flex-1">
                  <span className="block break-all text-[12.5px]">
                    {e.original_filename}
                  </span>
                  <span className="block text-[11px] text-faint">
                    {words(e.kind)} · {bytes(e.byte_size)}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </Field>
      </div>

      <div className="mt-4 rounded-md border border-line bg-surface-2 px-4 py-3">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            className="mt-0.5 accent-[color:var(--accent)]"
            checked={redacted}
            onChange={(e) => setRedacted(e.target.checked)}
          />
          <span>
            <span className="block font-head text-[12.5px] font-semibold">
              These items are already redacted
            </span>
            <span className="mt-0.5 block text-[11.5px] leading-relaxed text-faint">
              Tick this only if the files you are sending were redacted before
              they were attached. This system does not redact anything — the
              file that goes out is byte for byte the file that came in.
              Ticking it while sending an unredacted original puts a false
              statement in the disclosure register, and nothing can detect it.
            </span>
          </span>
        </label>

        {redacted && (
          <div className="mt-3">
            <Field label="What was removed, and why" hint="Optional, and recorded in the register.">
              <input
                className="field"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
          </div>
        )}
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!selected.length || !recipient}
          onClick={submit}
        >
          Send
        </Button>
      </div>
    </Modal>
  )
}

// ======================================================================
// WHAT YOU HAVE BEEN SENT — the recipient's own view
// ======================================================================
//
// A DIFFERENT SHAPE FROM THE EXHIBIT LIST, because it answers a
// different question. These rows exist because somebody with
// control_disclosure decided to send this person this item. `sent_at`
// and `first_opened_at` are the two facts the employer will rely on.

export function DisclosedToMe({ caseId, items }) {
  return (
    <Panel
      eyebrow="Sent to you"
      title="Documents you have been given"
    >
      {items.length === 0 ? (
        <Empty icon={FileStack} title="Nothing has been sent to you">
          When documents are shared with you on this case they appear here, with
          the date they were sent.
        </Empty>
      ) : (
        <ul className="space-y-2.5">
          {items.map((d) => (
            <DisclosedRow key={d.evidence_id} caseId={caseId} item={d} />
          ))}
        </ul>
      )}
    </Panel>
  )
}

function DisclosedRow({ caseId, item }) {
  const [progress, setProgress] = useState(null)
  const [error, setError] = useState(null)

  const fetchIt = async () => {
    setError(null)
    setProgress(0)
    try {
      // The filename and the size are passed IN rather than read from
      // the response headers, which this origin cannot see — see the
      // CORS note on download() in lib/api.js. Both come from the
      // evidence row the server built the headers from.
      await download(`/${caseId}/evidence/${item.evidence_id}/download`, {
        onProgress: setProgress,
        filename: item.original_filename,
        totalBytes: item.byte_size,
      })
    } catch (e) {
      setError(e)
    } finally {
      setProgress(null)
    }
  }

  return (
    <li className="panel-inset px-4 py-3.5">
      <Refusal error={error} onDismiss={() => setError(null)} />
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="break-all font-head text-[13px] font-semibold">
            {item.original_filename}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Chip>{words(item.kind)}</Chip>
            {/* "sent redacted", never "we redacted it". */}
            {item.redacted && <Chip tone="amber">sent as a redacted version</Chip>}
            {item.first_opened_at ? (
              <Chip tone="blue">first opened {dateTime(item.first_opened_at)}</Chip>
            ) : (
              <Chip tone="accent">not yet opened</Chip>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-faint">
            <span>Sent {dateTime(item.sent_at)}</span>
            <span>{bytes(item.byte_size)}</span>
            <span className="mono" title={item.sha256}>
              sha256 {shortHash(item.sha256)}
            </span>
          </div>
          {progress != null && (
            <div className="mt-3 max-w-xs">
              <Progress value={progress} label="Downloading" />
            </div>
          )}
        </div>
        <Button size="sm" icon={Download} busy={progress != null} onClick={fetchIt}>
          Download
        </Button>
      </div>
    </li>
  )
}
